import { AnimatePresence, motion } from "framer-motion";
import jsPDF from "jspdf";
import { formatDeliveryDate, getEstimatedDeliveryDate } from "../utils/orderUtils";

const OrderDetailsModal = ({ order, onClose, onCancelOrder, onTryAgain }) => {
    if (!order) return null;

    const getStatusColor = status => {
        const colors = {
            pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
            confirmed: "bg-blue-100 text-blue-800 border-blue-200",
            designing: "bg-purple-100 text-purple-800 border-purple-200",
            "proof-sent": "bg-indigo-100 text-indigo-800 border-indigo-200",
            "proof-approved": "bg-green-100 text-[#008000] border-green-200",
            printing: "bg-orange-100 text-orange-800 border-orange-200",
            "quality-check": "bg-teal-100 text-teal-800 border-teal-200",
            packed: "bg-cyan-100 text-cyan-800 border-cyan-200",
            shipped: "bg-blue-100 text-blue-800 border-blue-200",
            delivered: "bg-green-100 text-[#008000] border-green-200",
            cancelled: "bg-red-100 text-red-800 border-red-200",
            returned: "bg-gray-100 text-gray-800 border-gray-200",
        };
        return colors[status] || "bg-gray-100 text-gray-800 border-gray-200";
    };

    const formatDate = dateString => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDateForPDF = date => {
        return new Date(date).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatCurrencyForPDF = amount => {
        return `Rs. ${new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount)}`;
    };

    const generatePDF = () => {
        if (!order) return;

        const doc = new jsPDF();
        let yPos = 10;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 10;

        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("ORDER INVOICE", pageWidth / 2, yPos, { align: "center" });
        yPos += 6;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("Kigo Prints", pageWidth / 2, yPos, { align: "center" });
        yPos += 8;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 6;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Order Information", margin, yPos);
        yPos += 5;

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.text(`Order Number: ${order.orderNumber || order._id}`, margin, yPos);
        doc.text(`Order Date: ${formatDateForPDF(order.createdAt)}`, margin, yPos + 4);
        doc.text(`Status: ${order.status.replace("-", " ").toUpperCase()}`, margin + 90, yPos);
        if (order.isRush) {
            doc.text("Rush Order: Yes", margin + 90, yPos + 4);
        }
        yPos += 10;

        if (order.shippingAddress) {
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            doc.text("Shipping Address", margin, yPos);
            yPos += 5;

            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            if (order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store') {
                doc.text("Pick from store", margin, yPos);
                yPos += 6;
            } else {
                const fullName =
                    order.shippingAddress.fullName ||
                    `${order.shippingAddress.firstName || ""} ${order.shippingAddress.lastName || ""}`.trim();
                doc.text(fullName || "N/A", margin, yPos);
                yPos += 4;
                doc.text(`${order.shippingAddress.addressLine1 || order.shippingAddress.street || ""}`, margin, yPos);
                yPos += 4;
                if (order.shippingAddress.addressLine2) {
                    doc.text(`${order.shippingAddress.addressLine2}`, margin, yPos);
                    yPos += 4;
                }
                doc.text(
                    `${order.shippingAddress.city || ""}, ${order.shippingAddress.state || ""} ${order.shippingAddress.pincode || order.shippingAddress.postalCode || ""}`,
                    margin,
                    yPos
                );
                yPos += 4;
                if (order.shippingAddress.phone) {
                    doc.text(`Phone: ${order.shippingAddress.phone}`, margin, yPos);
                    yPos += 4;
                }
                yPos += 6;
            }
        }

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text("Order Items", margin, yPos);
        yPos += 5;

        if (order.items && order.items.length > 0) {
            doc.setFontSize(7);
            doc.setFont("helvetica", "bold");
            doc.text("Product", margin, yPos);
            doc.text("Qty", margin + 80, yPos);
            doc.text("Unit Price", margin + 100, yPos);
            doc.text("Total", margin + 150, yPos);
            yPos += 4;

            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(0.5);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 4;

            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            order.items.forEach(item => {
                if (yPos > pageHeight - 50) {
                    return;
                }

                const productName = item.product?.name || "Product Name";
                const quantity = item.quantity || 0;
                const unitPrice = item.unitPrice || 0;
                const totalPrice = item.totalPrice || 0;

                const lines = doc.splitTextToSize(productName, 70);
                doc.text(lines[0], margin, yPos);
                if (lines.length > 1) {
                    doc.text(lines[1], margin, yPos + 3);
                }

                doc.text(quantity.toString(), margin + 80, yPos);
                doc.text(formatCurrencyForPDF(unitPrice), margin + 100, yPos);
                doc.setFont("helvetica", "bold");
                doc.text(formatCurrencyForPDF(totalPrice), margin + 150, yPos);
                doc.setFont("helvetica", "normal");

                yPos += lines.length > 1 ? 7 : 5;
            });
        } else {
            doc.setFontSize(8);
            doc.text("No items found", margin, yPos);
            yPos += 5;
        }

        yPos += 5;

        const pricingStartX = margin + 100;
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("Pricing Details", pricingStartX, yPos);
        yPos += 5;

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        const subtotal = order.pricing?.subtotal || order.totalAmount || 0;
        doc.text("Subtotal:", pricingStartX, yPos);
        doc.setFont("helvetica", "bold");
        doc.text(formatCurrencyForPDF(subtotal), pageWidth - margin, yPos, { align: "right" });
        yPos += 4;

        if (order.pricing?.discount?.amount > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Discount:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(`-${formatCurrencyForPDF(order.pricing.discount.amount)}`, pageWidth - margin, yPos, {
                align: "right",
            });
            yPos += 4;
        }

        if (order.pricing?.tax?.amount > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Tax:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(formatCurrencyForPDF(order.pricing.tax.amount), pageWidth - margin, yPos, { align: "right" });
            yPos += 4;
        }

        if (order.pricing?.shipping?.cost > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Shipping:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(formatCurrencyForPDF(order.pricing.shipping.cost), pageWidth - margin, yPos, { align: "right" });
            yPos += 4;
        }

        if (order.rushCharges > 0) {
            doc.setFont("helvetica", "normal");
            doc.text("Rush Charges:", pricingStartX, yPos);
            doc.setFont("helvetica", "bold");
            doc.text(formatCurrencyForPDF(order.rushCharges), pageWidth - margin, yPos, { align: "right" });
            yPos += 4;
        }

        yPos += 2;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(pricingStartX, yPos, pageWidth - margin, yPos);
        yPos += 4;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        const total = (order.pricing?.total || order.totalAmount || 0) + (order.rushCharges || 0);
        doc.text("Total:", pricingStartX, yPos);
        doc.setFontSize(10);
        doc.text(formatCurrencyForPDF(total), pageWidth - margin, yPos, { align: "right" });
        yPos += 8;

        if (order.payment && yPos < pageHeight - 30) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Payment Information", margin, yPos);
            yPos += 5;

            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            doc.text(`Payment Method: ${order.payment.method || "N/A"}`, margin, yPos);
            doc.text(`Payment Status: ${order.payment.status?.toUpperCase() || "N/A"}`, margin + 90, yPos);
            yPos += 4;
            if (order.payment.ccavenueTrackingId) {
                doc.text(`CCAvenue Transaction ID: ${order.payment.ccavenueTrackingId}`, margin, yPos);
                yPos += 4;
            } else if (order.payment.razorpayPaymentId) {
                doc.text(`Razorpay Payment ID: ${order.payment.razorpayPaymentId}`, margin, yPos);
                yPos += 4;
            }
            yPos += 3;
        }

        if (order.specialInstructions && yPos < pageHeight - 20) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Special Instructions", margin, yPos);
            yPos += 5;

            const instructions = doc.splitTextToSize(order.specialInstructions, pageWidth - margin * 2);
            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            instructions.forEach(line => {
                if (yPos < pageHeight - 10) {
                    doc.text(line, margin, yPos);
                    yPos += 4;
                }
            });
        }

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(`Generated on ${new Date().toLocaleDateString("en-IN")}`, pageWidth / 2, pageHeight - 8, {
            align: "center",
        });

        const fileName = `Order-${order.orderNumber || order._id}-${new Date().toISOString().split("T")[0]}.pdf`;
        doc.save(fileName);
    };

    const getStatusIcon = status => {
        const icons = {
            pending: "⏳",
            confirmed: "✅",
            designing: "🎨",
            "proof-sent": "📝",
            "proof-approved": "👍",
            printing: "🖨️",
            "quality-check": "🔍",
            packed: "📦",
            shipped: "🚚",
            delivered: "🏠",
            cancelled: "❌",
            returned: "↩️",
        };
        return icons[status] || "📋";
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 rounded-t-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0 pr-2">
                                <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                                    Order #{order.orderNumber || order._id.slice(-8)}
                                </h2>
                                <p className="text-xs sm:text-sm text-gray-600">Placed on {formatDate(order.createdAt)}</p>
                            </div>
                            <button onClick={onClose} className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
                                <svg
                                    className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
                        {/* Current Status */}
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Current Status</h3>
                            <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap gap-1.5 sm:gap-2">
                                <span className="text-xl sm:text-2xl">{getStatusIcon(order.status)}</span>
                                <span
                                    className={`inline-flex px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full border ${getStatusColor(
                                        order.status
                                    )}`}
                                >
                                    {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace("-", " ")}
                                </span>
                                {order.payment?.status === "failed" && (
                                    <span className="inline-flex px-2 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full border bg-red-100 text-red-800 border-red-200">
                                        Payment Failed
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs sm:text-sm text-gray-600">Estimated Delivery:</span>
                                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                                        {formatDeliveryDate(getEstimatedDeliveryDate(order))}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Status History */}
                        {order.statusHistory && order.statusHistory.length > 0 && (
                            <div>
                                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Order Timeline</h3>
                                <div className="space-y-2 sm:space-y-3">
                                    {order.statusHistory
                                        .slice()
                                        .reverse()
                                        .map((status, index) => (
                                            <div key={index} className="flex items-start space-x-2 sm:space-x-3">
                                                <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                                                    <span className="text-xs sm:text-sm">{getStatusIcon(status.status)}</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                                                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                                                            {status.status.charAt(0).toUpperCase() +
                                                                status.status.slice(1).replace("-", " ")}
                                                        </span>
                                                        <span className="text-xs text-gray-500">
                                                            {formatDate(status.timestamp)}
                                                        </span>
                                                    </div>
                                                    {status.notes && (
                                                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{status.notes}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Order Items */}
                        <div>
                            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Order Items</h3>
                            <div className="space-y-2 sm:space-y-3">
                                {order.items?.map((item, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                                        <div className="flex items-start justify-between gap-2 sm:gap-4">
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm sm:text-base font-medium text-gray-900">
                                                    {item.product?.name || "Product"}
                                                </h4>
                                                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Quantity: {item.quantity}</p>
                                                {item.customizations && Object.keys(item.customizations).length > 0 && (
                                                    <div className="mt-1.5 sm:mt-2">
                                                        <p className="text-xs sm:text-sm font-medium text-gray-700">
                                                            Customizations:
                                                        </p>
                                                        <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1">
                                                            {Object.entries(item.customizations).map(([key, value]) => (
                                                                <span
                                                                    key={key}
                                                                    className="inline-block bg-gray-100 text-gray-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded"
                                                                >
                                                                    {key}: {value}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {item.designUpload && (
                                                    <div className="mt-1.5 sm:mt-2">
                                                        <p className="text-xs sm:text-sm font-medium text-gray-700">
                                                            Design File:
                                                        </p>
                                                        <p className="text-xs sm:text-sm text-gray-600 truncate">
                                                            {item.designUpload.originalName}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right ml-2 sm:ml-4 flex-shrink-0">
                                                <div className="text-sm sm:text-base font-semibold text-gray-900">₹{item.totalPrice}</div>
                                                <div className="text-xs sm:text-sm text-gray-500">₹{item.unitPrice} each</div>
                                            </div>
                                        </div>
                                    </div>
                                )) || <p className="text-xs sm:text-sm text-gray-500 text-center py-3 sm:py-4">No items found</p>}
                            </div>
                        </div>

                        {/* Shipping Address */}
                        {order.shippingAddress && (
                            <div>
                                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Shipping Address</h3>
                                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                    {order.deliveryType === 'self-pickup' || order.shippingAddress.addressLine1 === 'Pick from store' ? (
                                        <p className="text-sm sm:text-base font-medium text-gray-900">Pick from store</p>
                                    ) : (
                                        <>
                                            <p className="text-sm sm:text-base font-medium text-gray-900">
                                                {order.shippingAddress.fullName ||
                                                    `${order.shippingAddress.firstName || ""} ${order.shippingAddress.lastName || ""
                                                        }`.trim()}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                                                {order.shippingAddress.addressLine1 || order.shippingAddress.street}
                                            </p>
                                            {order.shippingAddress.addressLine2 && (
                                                <p className="text-xs sm:text-sm text-gray-600">{order.shippingAddress.addressLine2}</p>
                                            )}
                                            <p className="text-xs sm:text-sm text-gray-600">
                                                {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                                                {order.shippingAddress.pincode || order.shippingAddress.postalCode}
                                            </p>
                                            {order.shippingAddress.phone && (
                                                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Phone: {order.shippingAddress.phone}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Order Summary */}
                        <div>
                            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Order Summary</h3>
                            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                <div className="flex justify-between text-xs sm:text-sm">
                                    <span className="text-gray-600">Subtotal:</span>
                                    <span className="text-gray-900">₹{order.pricing?.subtotal || 0}</span>
                                </div>
                                {order.pricing?.discount?.amount > 0 && (
                                    <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Discount:</span>
                                        <span className="text-[#008000]">-₹{order.pricing.discount.amount}</span>
                                    </div>
                                )}
                                {order.pricing?.tax?.amount > 0 && (
                                    <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Tax:</span>
                                        <span className="text-gray-900">₹{order.pricing.tax.amount}</span>
                                    </div>
                                )}
                                {order.pricing?.shipping?.cost > 0 && (
                                    <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Shipping:</span>
                                        <span className="text-gray-900">₹{order.pricing.shipping.cost}</span>
                                    </div>
                                )}
                                {order.rushCharges > 0 && (
                                    <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Rush Charges:</span>
                                        <span className="text-orange-600">₹{order.rushCharges}</span>
                                    </div>
                                )}
                                <div className="border-t border-gray-200 pt-1.5 sm:pt-2">
                                    <div className="flex justify-between text-sm sm:text-base font-semibold">
                                        <span className="text-gray-900">Total:</span>
                                        <span className="text-gray-900">
                                            ₹{(order.pricing?.total || order.totalAmount || 0) + (order.rushCharges || 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Information */}
                        {order.payment && (
                            <div>
                                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Payment Information</h3>
                                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                    <div className="flex justify-between items-center flex-wrap gap-1">
                                        <span className="text-xs sm:text-sm text-gray-600">Payment Method:</span>
                                        <span className="text-xs sm:text-sm text-gray-900 capitalize">{order.payment.method}</span>
                                    </div>
                                    <div className="flex justify-between items-center mt-1.5 sm:mt-2 flex-wrap gap-1">
                                        <span className="text-xs sm:text-sm text-gray-600">Payment Status:</span>
                                        <span
                                            className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded ${order.payment.status === "paid"
                                                ? "bg-green-100 text-[#008000]"
                                                : order.payment.status === "failed"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-yellow-100 text-yellow-800"
                                                }`}
                                        >
                                            {order.payment.status.charAt(0).toUpperCase() +
                                                order.payment.status.slice(1)}
                                        </span>
                                    </div>
                                    {order.payment.paidAt && (
                                        <div className="flex justify-between items-center mt-1.5 sm:mt-2 flex-wrap gap-1">
                                            <span className="text-xs sm:text-sm text-gray-600">Paid At:</span>
                                            <span className="text-xs sm:text-sm text-gray-900">{formatDate(order.payment.paidAt)}</span>
                                        </div>
                                    )}
                                    {(order.payment.ccavenueTrackingId || order.payment.razorpayPaymentId) && (
                                        <div className="flex justify-between items-center mt-1.5 sm:mt-2 flex-wrap gap-1">
                                            <span className="text-xs sm:text-sm text-gray-600">
                                                {order.payment.ccavenueTrackingId ? "CCAvenue Transaction ID:" : "Razorpay Payment ID:"}
                                            </span>
                                            <span className="text-xs sm:text-sm text-gray-900 font-mono break-all">
                                                {order.payment.ccavenueTrackingId || order.payment.razorpayPaymentId}
                                            </span>
                                        </div>
                                    )}
                                    {order.fulfillment?.awb && (
                                        <div className="flex justify-between items-center mt-1.5 sm:mt-2 flex-wrap gap-1">
                                            <span className="text-xs sm:text-sm text-gray-600">Shipment:</span>
                                            <span className="text-xs sm:text-sm text-gray-900 font-mono break-all">
                                                {order.fulfillment.courier || 'Shiprocket'} · {order.fulfillment.awb}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Special Instructions */}
                        {order.specialInstructions && (
                            <div>
                                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Special Instructions</h3>
                                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                    <p className="text-xs sm:text-sm text-gray-700">{order.specialInstructions}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 rounded-b-xl">
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                            <button
                                onClick={onClose}
                                className="flex-1 bg-gray-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-gray-700 transition-colors"
                            >
                                Close
                            </button>
                            {order.status !== "pending" && order.status !== "cancelled" && (
                                <button
                                    onClick={generatePDF}
                                    className="bg-indigo-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
                                >
                                    <svg
                                        className="w-3 h-3 sm:w-4 sm:h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    <span>Download Invoice</span>
                                </button>
                            )}
                            {onTryAgain ? (
                                <button
                                    onClick={() => {
                                        onClose();
                                        onTryAgain();
                                    }}
                                    className="bg-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-blue-700 transition-colors"
                                >
                                    Try Again
                                </button>
                            ) : onCancelOrder ? (
                                <button
                                    onClick={() => {
                                        onClose();
                                        onCancelOrder();
                                    }}
                                    className="bg-red-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl hover:bg-red-700 transition-colors"
                                >
                                    Cancel Order
                                </button>
                            ) : null}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default OrderDetailsModal;
